import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import ApiService from "../services/apiService";

const BACKEND_ORIGIN = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

export default function CustomerManagement() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("latest"); // latest | oldest
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [modalMode, setModalMode] = useState("view"); // view, edit
  const [usernameError, setUsernameError] = useState("");
  const [editError, setEditError] = useState("");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    username: "",
    street: "",
    zone: "",
    barangay: "",
    city: ""
  });

  const resolveCustomerProfilePictureSrc = (profilePicture) => {
    if (!profilePicture) return null;

    const value = String(profilePicture).trim();
    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith('/')) return `${BACKEND_ORIGIN}${value}`;
    if (value.startsWith('uploads/')) return `${BACKEND_ORIGIN}/${value}`;
    if (value.startsWith('profile/')) return `${BACKEND_ORIGIN}/uploads/${value}`;
    if (value.startsWith('customer-profile/')) return `${BACKEND_ORIGIN}/uploads/${value}`;

    return `${BACKEND_ORIGIN}/uploads/profile/${value}`;
  };

  // Load customers data
  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const customersData = await ApiService.getCustomers();
      setCustomers(customersData || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      setError('Failed to load customers data');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter customers based on search and status
  const filteredCustomers = customers.filter(customer => {
    const query = (searchTerm || '').trim().toLowerCase();
    if (!query) return true;

    const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.toLowerCase();
    const addressText = [
      customer.street,
      customer.zone,
      customer.barangay,
      customer.city,
      customer.address,
      customer.house_number,
      customer.zip_code
    ].filter(Boolean).join(' ').toLowerCase();

    return fullName.includes(query) ||
      (customer.username || '').toLowerCase().includes(query) ||
      (customer.phone_number || '').includes(searchTerm) ||
      addressText.includes(query) ||
      (customer.email || '').toLowerCase().includes(query);
  });

  // Sort filtered customers by join date
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
    if (sortOrder === "latest") {
      return bTime - aTime; // latest first
    }
    return aTime - bTime; // oldest first
  });

  // Handle modal operations
  const openModal = (mode, customer = null) => {
    setModalMode(mode);
    setSelectedCustomer(customer);

    if (mode === "edit" && customer) {
      setFormData({
        first_name: customer.first_name || "",
        last_name: customer.last_name || "",
        username: customer.username || "",
        street: customer.street || "",
        zone: customer.zone || "",
        barangay: customer.barangay || "",
        city: customer.city || ""
      });
      setUsernameError("");
      setEditError("");
    }
  };

  const closeModal = () => {
    setSelectedCustomer(null);
    setModalMode("view");
    setUsernameError("");
    setEditError("");
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (modalMode === "edit" && selectedCustomer) {
        // Only send fields the backend allows updating
        const payload = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          username: formData.username,
          street: formData.street,
          zone: formData.zone,
          barangay: formData.barangay,
          city: formData.city
        };
        await ApiService.updateCustomer(selectedCustomer.customer_id, payload);
      }
      
      await loadCustomers(); // Reload customers
      closeModal();
    } catch (error) {
      console.error('Error saving customer:', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to save customer data';
      setError(message);
      setEditError(message);
    }
  };

  // Deactivate action removed per request

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format address
  const formatAddress = (customer) => {
    const addressParts = [
      customer.street,
      customer.zone && `Zone ${customer.zone}`,
      customer.barangay && `Brgy. ${customer.barangay}`,
      customer.city
    ].filter(Boolean);
    
    return addressParts.length > 0 ? addressParts.join(', ') : 'Address not provided';
  };

  if (loading) {
    return (
      <Layout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e2e8f0',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <p style={{ color: '#64748b', fontSize: '16px' }}>Loading customers data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <style>{`
        @media (max-width: 767px) {
          .customer-table-wrapper {
            display: none !important;
          }
          .customer-cards-wrapper {
            display: grid !important;
            gap: 12px;
          }
          .customer-header-shell {
            padding: 18px 18px 16px !important;
          }
          .customer-header-title {
            font-size: 24px !important;
            line-height: 1.2 !important;
          }
          .customer-header-subtitle {
            font-size: 14px !important;
          }
          .customer-header-meta {
            font-size: 12px !important;
            line-height: 1.5 !important;
          }
          .customer-filter-bar {
            padding: 14px 14px !important;
            gap: 12px !important;
          }
          .customer-filter-bar .customer-search-wrap {
            min-width: 100% !important;
          }
        }

        @media (max-width: 384px) {
          .customer-header-title {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 8px !important;
          }
          .customer-header-title-icon {
            width: 30px !important;
            height: 30px !important;
          }
        }

        @media (min-width: 768px) {
          .customer-cards-wrapper {
            display: none !important;
          }
        }
      `}</style>
      <div>
        {/* Header Section */}
        <div className="customer-header-shell" style={{
          backgroundColor: 'white',
          borderRadius: '14px',
          padding: '24px 28px',
          marginBottom: '24px',
          boxShadow: '0 8px 24px rgba(37, 99, 235, 0.18)',
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          color: 'white'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h1 className="customer-header-title" style={{
                margin: 0,
                fontSize: '30px',
                fontWeight: '800',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                lineHeight: 1.2,
                flexWrap: 'wrap'
              }}>
                <span className="customer-header-title-icon" style={{
                  width: '36px',
                  height: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.18)',
                  borderRadius: '10px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  flexShrink: 0
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a8.38 8.38 0 0 1 13 0"/></svg>
                </span>
                <span>Customer Management</span>
              </h1>

              <p className="customer-header-subtitle" style={{
                margin: '10px 0 0 0',
                fontSize: '16px',
                opacity: 0.92,
                lineHeight: 1.5
              }}>
                Manage all registered customers on the eLaba platform
              </p>

              <p className="customer-header-meta" style={{
                margin: '8px 0 0 0',
                fontSize: '14px',
                opacity: 0.82,
                lineHeight: 1.5,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                <span>Total Customers: {customers.length}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{ color: '#dc2626', marginRight: '8px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </span>
            <span style={{ color: '#dc2626', fontSize: '14px' }}>{error}</span>
          </div>
        )}

        {!selectedCustomer || modalMode !== 'edit' ? (
          <div className="customer-filter-bar" style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '25px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            gap: '20px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <div className="customer-search-wrap" style={{ flex: 1, minWidth: '300px' }}>
              <input
                type="text"
                placeholder="Search customers by name, username, phone, or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Sort:</span>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="latest">Latest → Oldest</option>
                <option value="oldest">Oldest → Latest</option>
              </select>
            </div>
          </div>
        ) : null}

        {selectedCustomer && modalMode === 'edit' ? (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 12px 30px rgba(15, 23, 42, 0.06)', border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '24px' }}>
            <div style={{ padding: '18px 20px 14px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, marginBottom: '4px' }}>Edit Customer</div>
                  <div style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>
                    {`${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() || 'Customer Profile'}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={closeModal}
                    style={{
                      padding: '9px 14px',
                      border: '1px solid #94a3b8',
                      background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                      color: '#0f172a',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: '800',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)'
                    }}
                  >
                    Back to list
                  </button>
                </div>
              </div>
            </div>

            <div style={{ padding: '18px 20px 20px' }}>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '860px', margin: '0 auto' }}>
                {editError && (
                  <div style={{
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    color: '#dc2626'
                  }}>
                    {editError}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Username *
                    </label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => {
                        const next = e.target.value.trim();
                        setFormData({ ...formData, username: next });
                        if (selectedCustomer) {
                          const taken = customers.some(c => c.username && c.username.toLowerCase() === next.toLowerCase() && c.customer_id !== selectedCustomer.customer_id);
                          setUsernameError(taken ? 'Username already exists' : '');
                        }
                      }}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                    {usernameError && (
                      <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#ef4444' }}>{usernameError}</p>
                    )}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Street
                    </label>
                    <input
                      type="text"
                      value={formData.street}
                      onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Zone
                    </label>
                    <input
                      type="text"
                      value={formData.zone}
                      onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      Barangay
                    </label>
                    <input
                      type="text"
                      value={formData.barangay}
                      onChange={(e) => setFormData({ ...formData, barangay: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                      City
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '16px',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="button"
                    onClick={closeModal}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      color: '#64748b',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#64748b';
                      e.target.style.color = '#1e293b';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = '#e2e8f0';
                      e.target.style.color = '#64748b';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      color: 'white',
                      fontSize: '16px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      boxShadow: '0 8px 20px rgba(59, 130, 246, 0.28)'
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div style={{ width: '100%', marginBottom: '30px' }}>
            {filteredCustomers.length > 0 ? (
              <>
                <div className="customer-table-wrapper" style={{ width: '100%', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', color: 'white' }}>
                        <th style={{ padding: '14px', textAlign: 'left' }}>Name</th>
                        <th style={{ padding: '14px', textAlign: 'left' }}>Username</th>
                        <th style={{ padding: '14px', textAlign: 'left' }}>Phone</th>
                        <th style={{ padding: '14px', textAlign: 'left' }}>Address</th>
                        <th style={{ padding: '14px', textAlign: 'left' }}>Joined</th>
                        <th style={{ padding: '14px', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCustomers.map((customer) => (
                        <tr key={customer.customer_id} style={{ borderBottom: '1px solid #e2e8f0', transition: 'background 0.2s' }}>
                          <td style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {customer.profile_picture ? (
                              <img src={resolveCustomerProfilePictureSrc(customer.profile_picture)} alt={customer.first_name} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                            ) : (
                              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a8.38 8.38 0 0 1 13 0"/></svg>
                            )}
                            <span>{`${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unnamed Customer'}</span>
                          </td>
                          <td style={{ padding: '12px' }}>{customer.username || 'Not set'}</td>
                          <td style={{ padding: '12px' }}>{customer.phone_number || 'Not provided'}</td>
                          <td style={{ padding: '12px' }}>{formatAddress(customer)}</td>
                          <td style={{ padding: '12px' }}>{formatDate(customer.created_at)}</td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <button
                              onClick={() => openModal("edit", customer)}
                              style={{
                                marginRight: 6,
                                padding: '6px 12px',
                                border: '1px solid #10b981',
                                borderRadius: '6px',
                                backgroundColor: 'transparent',
                                color: '#10b981',
                                fontSize: '13px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={e => { e.target.style.backgroundColor = '#10b981'; e.target.style.color = 'white'; }}
                              onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#10b981'; }}
                            >Edit</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="customer-cards-wrapper" style={{ display: 'none' }}>
                  {sortedCustomers.map((customer) => (
                    <div key={customer.customer_id} style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {customer.profile_picture ? (
                            <img src={resolveCustomerProfilePictureSrc(customer.profile_picture)} alt={customer.first_name} style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                          ) : (
                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a8.38 8.38 0 0 1 13 0"/></svg>
                          )}
                          <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 700 }}>{`${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unnamed Customer'}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => openModal("edit", customer)}
                            style={{
                              padding: '6px 10px',
                              border: '1px solid #10b981',
                              borderRadius: 6,
                              backgroundColor: 'transparent',
                              color: '#10b981',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >Edit</button>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gap: 10 }}>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Username</div>
                          <div style={{ fontSize: 13, color: '#334155' }}>{customer.username || 'Not set'}</div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Phone</div>
                            <div style={{ fontSize: 13, color: '#334155' }}>{customer.phone_number || 'Not provided'}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Joined</div>
                            <div style={{ fontSize: 13, color: '#334155' }}>{formatDate(customer.created_at)}</div>
                          </div>
                        </div>

                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Address</div>
                          <div style={{ fontSize: 13, color: '#334155' }}>{formatAddress(customer)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  margin: '0 auto 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f1f5f9',
                  borderRadius: '50%',
                }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a8.38 8.38 0 0 1 13 0"/></svg>
                </div>
                <h3 style={{ margin: '0 0 10px 0', fontSize: '20px', color: '#1e293b' }}>
                  {searchTerm ? 'No customers found' : 'No customers registered yet'}
                </h3>
                <p style={{ margin: 0, color: '#64748b', fontSize: '16px' }}>
                  {searchTerm ? 'Try adjusting your search' : 'Customers will appear here as they register on the platform'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}